import { useEffect, useMemo, useRef, useState } from "react";
import { createTranscription, uploadAudioFile, uploadTranscription } from "../../../services/transcript.service";
import useSessionStore from "../../../store/useSessionStore";
import { useInput } from "../../../hooks/useInput";
import { updateData, useConditionedNavigator } from "../../../store/ConditionenNavigationProvider";
import { FileWithMetadata, formatTime, getDuration, getFileSize, useFileSelector } from "../../../components/globals/File";
import { useMutationAdapter, useQueryAdapter } from "../../../hooks/useQueries";
import { getIteractionById, getSessionsByFilters } from "../../../services/teacherSession.service";
import { clearXML, extractOptions } from "../../../services/chat.service";
import { getMetaData, getObjectBykey } from "../../../services/aws.service";
import { useChat } from "../../../store/chatProvider";
import { useTranslator } from "../../../hooks/useTranslator";
import { getClassProfileById } from "../../../services/profile-class.service";
import { useChatInput } from "../../../components/globals/chat-input";
import { useLLM } from "../../../hooks/useLLM";
import { storeAssetByCourseProfile } from "../../../services/asset.service";
import { useNavigate } from "react-router-dom";
import useGlobalStore from "../../../store/useGlobalStore";
import { v4 as uuidv4 } from "uuid";

export const key_hidden = '<__hidden__>';


/**
 * Crea una entrada para el paso 2 del proceso con la información proporcionada.
 *
 * @param {Object} params - Los parámetros para crear la entrada.
 * @param {string} params.filetxt - La transcripción de la sesión implementada.
 * @param {Array<Object>} params.messages - Los mensajes relacionados con la sesión.
 * @param {Object} params.classProfile - El perfil de la clase.
 * @param {Object} params.dataPrevPlan - Los datos del plan previo.
 * @returns {string} La entrada formateada para el paso 2.
 */
const createInputStep2 = ({ filetxt, messages, classProfile, dataPrevPlan }) => {
    const messagesStart = messages?.reverse?.()?.find(i => i.content.includes(key_hidden))?.content;
    let stage = extractOptions(messages?.slice?.(-1)?.[0]?.content ?? "", [/<stage_1>(.*?)<\/stage_1>/gs]);
    stage = stage?.[0]?.trim();
    let student_level = {};
    let prevPlan = extractOptions(dataPrevPlan?.data?.plan, [/<Fases_Selection>(.*?)<\/Fases_Selection>/gs]);
    prevPlan = prevPlan?.[0]?.trim();
    prevPlan = clearXML(prevPlan);
    try {
        student_level = JSON.parse(classProfile?.student_level);
        student_level = student_level?.[0];
    } catch (error) {
    }
    const input = `
    ${messagesStart}
    <stage_1>
    ${stage}
    </stage_1>
    Contexto del grupo:
    * Nombre del grupo: ${classProfile?.class_name ?? ""}
    * Área de enseñanza: ${getAreaName(classProfile)}
    * Tipo de agrupación de los estudiantes: ${student_level?.group_type ?? ""}
    * Nivel(es) de los estudiantes: ${student_level?.levels?.map(i => i.label).filter(Boolean).join?.(', ') ?? ""}
    * Número de estudiantes en cada nivel:
    ${student_level?.levels?.map(i => `    ${i.label}: ${i.studentsNumberGroup} estudiantes`).join('\n')}
    * Consideraciones para este grupo: ${classProfile?.considerations?.map(i => i.description).filter(Boolean).join?.(', ') ?? ""}

    Planeación inicial de la sesión:
    ${prevPlan}

    Trancripción de la sesión implementada:
    ${filetxt}
    
    ${key_hidden}
                    `;
    return input;
}

/**
 * Hook personalizado para manejar los detalles de audio.
 *
 * @returns {Object} Un objeto que contiene:
 * - value: El valor actual del audio.
 * - bind: Función para enlazar el input.
 * - duration: La duración del audio en formato de tiempo.
 * - currentItem: El elemento actual.
 * - dispatch: Función para despachar acciones.
 */
export const useDetailsAudio = () => {
    const { currentItem, dispatch } = useConditionedNavigator();
    const { bind, setValue: setValueInput } = useInput();
    const [value, setValue] = useState();
    const [duration, setDuration] = useState('...');
    useEffect(() => {
        if (value?.params?.Metadata?.['x-amz-meta-duration']) setDuration(formatTime(+(value?.params?.Metadata?.['x-amz-meta-duration'] ?? 0)));
    }, [value?.params]);
    useEffect(() => {
        if (currentItem?.key) {
            getMetaData(currentItem?.key).then((data) => setValue({ ...data, file: null }));
        }
    }, [currentItem]);
    useEffect(() => {
        if (value?.params?.Key) {
            setValueInput(value?.params?.Key?.split('.')?.[0]?.split('_')?.reverse?.()?.[0]);
        }
    }, [value, setValueInput]);

    return { value, bind, duration, currentItem, dispatch };
}

/**
 * Hook personalizado para crear una transcripción.
 *
 * @returns {Object} - Retorna un objeto con la función `createTranscription`.
 */
export const useCreateTranscription = () => {
    const { currentItem, dispatch } = useConditionedNavigator();

    const onDataChangePreproces = (data) => {
        updateData(dispatch, { trancriptionprocesses: data });
        if (data?.event === 'end') updateData(dispatch, { trancriptionprocesses: null, keytranscription: data?.Key });
    }
    const _createTranscription = async () => {
        await createTranscription(currentItem?.key, { onDataChange: onDataChangePreproces });
    }
    return { createTranscription: _createTranscription }
}

/**
 * Hook personalizado para manejar la carga de archivos.
 *
 * @returns {Object} - Retorna un objeto con las funciones y datos necesarios para manejar la carga de archivos.
 * @property {Function} handleOnClick - Función que se ejecuta al hacer clic para iniciar la carga del archivo.
 * @property {Object} currentData - Datos actuales del ítem seleccionado.
 * @property {Object} bind - Propiedades de enlace para el input.
 * @property {Function} dispatch - Función para despachar acciones al estado.
 */
export const useUploadFile = () => {
    const { currentItem: currentData, dispatch } = useConditionedNavigator();
    const { user } = useSessionStore();
    const { value, bind } = useInput();
    const handleDispatch = (evt) => {
        if (evt?.type === "UPDATE_PROGRESS") updateData(dispatch, { progress: evt?.payload });
        else if (evt?.type === "UPLOAD_SUCCESS") updateData(dispatch, { uploading: false, succes: true, key: evt?.payload });
        else if (evt?.type === "UPLOAD_FAILURE") updateData(dispatch, { uploading: false, succes: false, messageError: { text: evt?.payload } });
    }
    const handleOnClick = () => {
        updateData(dispatch, { uploading: true });
        const createKey = (userData, timestamp, name, customName) => {
            const fileExtension = name.split(".").pop();
            const fileName = `${userData.id}_${timestamp}_${customName}.${fileExtension}`;
            return 'original/' + currentData.idClassProfile + '/' + fileName;
        }
        uploadAudioFile(user, currentData.file, value?.trim?.(), handleDispatch, createKey, { session_id: currentData?.plan?.value, ...currentData?.plan });
    }
    return { handleOnClick, currentData, bind, dispatch }
}

/**
 * Hook personalizado para seleccionar un archivo y validar su tamaño y duración.
 *
 * @param {Object} params - Parámetros del hook.
 * @param {number} params.sizeinmb - Tamaño máximo permitido del archivo en megabytes.
 * @returns {Object} - Retorna un objeto con las propiedades `bind` y `dispatch`.
 *
 * @property {Function} bind - Función para enlazar el selector de archivos.
 * @property {Function} dispatch - Función para despachar acciones al navegador condicionado.
 *
 * @example
 * const { bind, dispatch } = useSelectFile({ sizeinmb: 10 });
 */
export const useSelectFile = ({ sizeinmb }) => {
    const { bind, value } = useFileSelector({ accept: "ogg,.mp4,.mp3,.m4a,.wav" });
    const { dispatch } = useConditionedNavigator();
    useEffect(() => {
        if (value) {
            const size = getFileSize(value);
            if ((size?.sizeingb && (size?.sizeingb * 1024) > sizeinmb) || size?.sizeinmb > sizeinmb) updateData(dispatch, { file: value, succes: false, messageError: { text: "enad_feed_back_file_not_allowed", translator: { size: sizeinmb } } });
            else getDuration(value).then((duration) => updateData(dispatch, { succes: true, messageError: null, file: FileWithMetadata.fromAudioFile(value, duration) }));
        }
    }, [value, dispatch, sizeinmb]);
    return { bind, dispatch }
}

/**
 * Obtiene un objeto por clave.
 *
 * @param {string} key - La clave del objeto a obtener.
 * @returns {Promise<Object>} Una promesa que resuelve con el objeto obtenido.
 * @async
 */
export const _getObjectBykey = async (key) => {
    const data = await getObjectBykey(key, { responseType: "application/json", bufferToFile: (data) => data });
    return JSON.parse(data?.file);
}

/**
 * Hook personalizado para editar transcripciones.
 *
 * @returns {Object} Retorna un objeto con las siguientes propiedades y funciones:
 * @property {boolean} isLoading - Indica si los datos están cargando.
 * @property {Object} data - Datos de la transcripción.
 * @property {Function} updateData - Función para actualizar los datos de la transcripción.
 * @property {Function} save - Función para guardar la transcripción.
 * @property {Function} refetch - Función para volver a obtener los datos de la transcripción.
 * @property {Object} user - Información del usuario.
 * @property {Function} goToFeedBack - Función para navegar a la página de retroalimentación.
 * @property {Object} metadatafile - Metadatos del archivo.
 */
export const useEditTranscript = () => {
    const { currentItem } = useConditionedNavigator();
    const { setSuperPowerconection } = useGlobalStore();
    const navigate = useNavigate();
    const { user } = useSessionStore();
    const [data, setData] = useState();
    const { data: metadatafile } = useQueryAdapter({ fnquery: getMetaData, data: currentItem?.key });
    const { isLoading, data: dataRes, refetch } = useQueryAdapter({ fnquery: _getObjectBykey, data: currentItem?.keytranscriptionedit });
    useEffect(() => {
        if (dataRes) setData(JSON.parse(JSON.stringify(dataRes)));
    }, [dataRes]);
    const save = async ({ preventRefetch, data: _data }) => {
        await uploadTranscription(currentItem?.keytranscriptionedit, _data ?? data);
        if (!preventRefetch) refetch();
    }
    const updateData = (index, dataupdate) => {
        let { utterances } = data;
        utterances = utterances?.map((item, i) => i === index ? { ...item, ...dataupdate } : item);
        const _data = { ...data, utterances };
        setData(() => _data)
        save({ preventRefetch: true, data: _data });
    };
    const goToFeedBack = () => {
        const _goToFeedBack = async () => {
            setSuperPowerconection({ idClassProfile: currentItem?.idClassProfile, keytranscription: currentItem?.keytranscriptionedit });
            await save({ target:'feedback',preventRefetch: true, data: { ...data, session_plan_id: metadatafile?.params?.Metadata?.['x-amz-meta-plan-id'] ?? null } });
            navigate('/feedback');
        }
        _goToFeedBack();
    }
    return { isLoading, data, updateData, save, refetch, user, goToFeedBack, metadatafile }
}

/**
 * Hook personalizado para obtener y procesar las sesiones filtradas.
 *
 * @returns {Object} Un objeto que contiene los datos de la consulta y las opciones procesadas.
 * @returns {Object} querieres - El objeto retornado por el adaptador de consultas.
 * @returns {Array} querieres.data - Los datos obtenidos de la consulta.
 * @returns {Array} options - Las opciones procesadas a partir de los datos de la consulta.
 */
export const useSessionClass = () => {
    const querieres = useQueryAdapter({ fnquery: getSessionsByFilters, data: null, name: "getSessionsByFilters", staleTime: 0 });
    const { data } = querieres;
    const options = useMemo(() => data?.map((item) => ({ label: extractOptions(item?.plan, [/<title>(.*?)<\/title>/gs])?.[0]?.trim?.(), value: item?.session_id }))?.filter(i => Boolean(i.label)), [data]);
    return { ...querieres, options }
}

/**
 * Hook que obtiene una iteración por ID.
 *
 * @param {Object} params - Parámetros para el hook.
 * @param {string} params.sessionId - ID de la sesión para obtener la iteración.
 * @param {boolean} params.isAsync - Indica si la consulta debe ser asíncrona.
 * @returns {Object} - Retorna un objeto con el estado de carga, error, datos, función de consulta asíncrona y función para establecer datos.
 * @returns {boolean} isLoading - Indica si la consulta está en proceso.
 * @returns {boolean} isError - Indica si hubo un error en la consulta.
 * @returns {any} data - Datos obtenidos de la consulta.
 * @returns {Function} asyncFetch - Función para realizar la consulta asíncrona.
 * @returns {Function} setData - Función para establecer los datos manualmente.
 */
export const useGetIterationById = ({ sessionId, isAsync }) => {
    const { isLoading, isError, data, asyncFetch, setData } = useQueryAdapter({
        name: `getIterationById__feedback`,
        data: sessionId,
        fnquery: async (id) => await getIteractionById(id).catch(() => { }),
        isAsync,
    });
    return { isLoading, isError, data, asyncFetch, setData };
};

/**
 * Hook que obtiene el perfil de una clase por su ID.
 *
 * @param {Object} params - Parámetros del hook.
 * @param {string} params.idClassProfile - ID del perfil de la clase.
 * @param {boolean} params.isAsync - Indica si la consulta debe ser asíncrona.
 * @returns {Object} - Retorna un objeto con las propiedades isLoading, isError, data, asyncFetch y setData.
 * @returns {boolean} isLoading - Indica si la consulta está en proceso.
 * @returns {boolean} isError - Indica si hubo un error en la consulta.
 * @returns {Object} data - Datos obtenidos de la consulta.
 * @returns {Function} asyncFetch - Función para realizar la consulta de forma asíncrona.
 * @returns {Function} setData - Función para establecer los datos manualmente.
 */
export const useGetClassProfileById = ({ idClassProfile, isAsync }) => {
    const { isLoading, isError, data, asyncFetch, setData } = useQueryAdapter({
        name: `getClassProfileById__feedback`,
        data: idClassProfile ? { id: idClassProfile } : null,
        fnquery: async (id) => await getClassProfileById(id).catch(() => { }),
        isAsync,
    });
    return { isLoading, isError, data, asyncFetch, setData };
};

/**
 * Hook personalizado para manejar solicitudes LLM y mensajes de chat.
 *
 * @returns {Object} - Retorna un objeto con las siguientes propiedades:
 *   - sendMessage: {Function} Función para enviar un mensaje.
 *   - isLoading: {boolean} Indica si la solicitud está en curso.
 *   - called: {boolean} Indica si la solicitud ha sido llamada.
 *   - setStep: {Function} Función para establecer el paso actual.
 */
export const useLLMReq = () => {
    const [step, setStep] = useState(1);
    const { send, isLoading, called, setLastRequest, lastRequest } = useLLM({
        createBody: ({ message, sessionId, class_profile_id, step: _step, parent_session_id }, { i18n, lastRequest }) => {
            return {
                messages: [message],
                type: `feedback-enad_step_${_step}`,
                session_id: sessionId ?? lastRequest?.session_id,
                lang: i18n.language,
                class_profile_id: class_profile_id ?? null,
                parent_session_id: (parent_session_id ?? lastRequest?.parent_session_id) ?? null,
                asset_ids: [],
                response_type: null,
                response_schema: null
            }
        }
    });
    const { addNewMessage, state, setState } = useChat();
    const { dataSession } = state;
    useEffect(() => {
        setState({ lastRequest });
    }, [lastRequest, setState]);
    useEffect(() => {
        setLastRequest({ ...(dataSession?.data ?? {}) });
    }, [dataSession, setLastRequest]);
    const sendMessage = async (data) => {
        const message = { role: 'user', content: data?.text };
        addNewMessage(message);
        if (data.step) setStep(data.step);
        const res = await send({ ...data, message, step: data.step ?? step });
        addNewMessage(res);
    }
    return { sendMessage, isLoading, called, setStep }
}

/**
 * Obtiene el nombre del área basado en el perfil de la clase.
 *
 * @param {Object} classProfile - El perfil de la clase.
 * @param {Array} classProfile.subjectAreas - Las áreas temáticas del perfil de la clase.
 * @param {string} classProfile.student_level - El nivel del estudiante en formato JSON.
 * @returns {string} El nombre del área o una cadena vacía si no se encuentra.
 */
export const getAreaName = (classProfile) => {
    try {
        const { subjectAreas, student_level } = classProfile;
        if (subjectAreas?.length) return subjectAreas.map((item) => item?.name).filter(Boolean).join?.(', ');
        const _student_level = JSON.parse(student_level);
        return _student_level?.knowledge_area ?? "";
    } catch (error) {
    }
    return '';
}

/**
 * Hook personalizado para manejar la pantalla de retroalimentación del chat.
 *
 * @returns {Object} - Retorna un objeto con las referencias y estados necesarios para la pantalla de retroalimentación del chat.
 * @property {Object} refScroll - Referencia al elemento de scroll.
 * @property {Array} messages - Lista de mensajes del chat.
 * @property {Object} state - Estado del chat.
 * @property {Function} bindChat - Función para enlazar el input del chat.
 * @property {boolean} isLoading - Indica si hay una carga en progreso.
 */
export const useFeedBackScreenChat = () => {
    const refScroll = useRef(null);
    const { state } = useChat();
    const navigate = useNavigate();
    const { messages, sessionId, classProfile, jsonTrancription, dataPrevPlan } = state;
    const { sendMessage, isLoading, called } = useLLMReq();
    const { user } = useSessionStore();
    const { i18n } = useTranslator();
    const { isLoading: isUpLoadingFile } = useMutationAdapter(storeAssetByCourseProfile);
    const { bind: bindChat } = useChatInput({
        sendMessage: async (text, { clearMessage }) => {
            await sendMessage({ text, class_profile_id: classProfile?.id });
            clearMessage();
        }
    });
    useEffect(() => {
        if (!messages?.length && user?.id && !isLoading && !called) {
            const title = extractOptions(dataPrevPlan?.data?.plan, [/<title>(.*?)<\/title>/gs])?.[0]?.trim?.();
            const messageStart = `
                Nombre del docente: ${user.first_name}
                Área de enseñanza: ${getAreaName(classProfile)}
                Fecha de la sesión retroalimentada: ${title}
                idioma de la plataforma:${i18n.language === 'es' ? 'Español' : 'Inglés'}
                ${key_hidden}
            `
            sendMessage({ sessionId, text: messageStart, class_profile_id: classProfile?.id });
        }
    }, [messages, user, classProfile, isLoading, sendMessage, i18n, sessionId, called, dataPrevPlan]);

    const filetxt = useMemo(() => {
        if (jsonTrancription?.utterances?.length) {
            const { utterances } = jsonTrancription;
            const text = utterances?.map((item) => `${item?.speaker}: ${item?.text}`).join('\n');
            return text;
        }
        return null;
    }, [jsonTrancription]);

    /**
     * Se revisa constante mente si el primer mensaje es del asistente y si contiene la palabra clave <stage_1> se cambia de paso
     */
    useEffect(() => {
        if ((messages?.[0]?.type === "ai" || messages?.[0]?.role === "assistant") && messages?.[0]?.content?.includes('<stage_1>')) {
            const changeStep = async () => {
                const newSessionId = uuidv4();
                await sendMessage({ sessionId: newSessionId, parent_session_id: sessionId, text: createInputStep2({ filetxt, messages, classProfile, dataPrevPlan }), asset_id: null, class_profile_id: classProfile?.id, step: 2 });
                navigate('/feedback?sessionId=' + newSessionId);
            }
            changeStep();
        }
    }, [messages, filetxt, classProfile, sessionId, sendMessage, navigate, dataPrevPlan]);

    return { refScroll, messages, state, bindChat, isLoading: isLoading || isUpLoadingFile };
}

/**
 * Hook para cargar datos de utilidades.
 * 
 * @param {Object} params - Parámetros para cargar los datos.
 * @param {string} params.idClassProfile - ID del perfil de la clase.
 * @param {string} params.keytranscription - Clave de la transcripción.
 * @param {string} params.sessionId - ID de la sesión.
 * 
 * @returns {Object} - Objeto con el estado de carga y los datos cargados.
 * @returns {boolean} returns.isLoading - Indica si los datos están cargando.
 * @returns {Object} returns.data - Datos cargados.
 * @returns {string} returns.data.sessionId - ID de la sesión.
 * @returns {Array} returns.data.messages - Mensajes de la sesión.
 * @returns {Object} returns.data.dataSession - Datos de la sesión.
 * @returns {Object} returns.data.classProfile - Perfil de la clase.
 * @returns {string} returns.data.keytranscription - Clave de la transcripción.
 * @returns {Object} returns.data.jsonTrancription - Datos de la transcripción.
 * @returns {Object} returns.data.dataPrevPlan - Datos de la planificación previa.
 */
export const useLoadUtilsData = ({ idClassProfile, keytranscription, sessionId }) => {
    const { isLoading: isLoadingIteration, data: dataSession } = useGetIterationById({ sessionId });
    const { isLoading: isLoadingClass, data: classProfile, setData: setDataReq } = useGetClassProfileById({ idClassProfile: null });
    const { isLoading: isLoadingTrancript, data: jsonTrancription } = useQueryAdapter({ fnquery: _getObjectBykey, data: keytranscription });
    const { isLoading: isLoadingPrevPlanning, data: dataPrevPlan, setData: setDataPrevPlaning } = useGetIterationById({ sessionId: null });
    useEffect(() => {
        if (idClassProfile || dataSession?.data?.class_profile?.id) {
            setDataReq({ id: idClassProfile ?? dataSession?.data?.class_profile?.id });
        }
    }, [idClassProfile, dataSession, setDataReq]);
    useEffect(() => {
        if (jsonTrancription?.session_plan_id) {
            setDataPrevPlaning(jsonTrancription?.session_plan_id);
        }
    }, [jsonTrancription, setDataPrevPlaning]);
    const _data = useMemo(() => {
        let messages = [...(dataSession?.data?.messages ?? []), ...(dataSession?.history?.reduce((acc, i) => [...acc, ...i.messages], []) ?? [])];
        messages = messages?.sort?.((a, b) => new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0))?.slice(0, -1);
        return { sessionId, messages, dataSession, classProfile, keytranscription, jsonTrancription, dataPrevPlan };
    }, [dataSession, classProfile, sessionId, keytranscription, jsonTrancription, dataPrevPlan]);
    return { isLoading: isLoadingIteration || isLoadingClass || isLoadingPrevPlanning || isLoadingTrancript, data: _data };
}

/**
 * Hook personalizado para manejar la sesión de retroalimentación en la pantalla de Enad.
 *
 * @returns {Object} Retorna un objeto con las siguientes propiedades:
 * - classProfile {Object|null}: El perfil de la clase.
 * - dataPlanning {Object|null}: Los datos de planificación.
 * - isLoadingSession {boolean}: Indicador de carga de la sesión.
 * - isLoadingSupper {boolean}: Indicador de carga del perfil de la clase.
 * - setClassProfile {Function}: Función para establecer el perfil de la clase.
 */
export const useFeedBackSessionEnadScreen = () => {
    const [classProfile, setClassProfile] = useState(null);
    const { superPowerconection, setSuperPowerconection } = useGlobalStore();
    const { mutateAsync: mutateFeedBack, isLoading: isLoadingSession } = useMutationAdapter(getIteractionById);
    const { mutateAsync: mutateAsyncClassProfile, isLoading: isLoadingSupper } = useMutationAdapter(getClassProfileById);
    const [dataPlanning, setDataPlanning] = useState(null);
    useEffect(() => {
        const _getData = async () => {
            if(superPowerconection?.target !== 'feedback') return;
            if (superPowerconection?.sessionId) {
                await mutateFeedBack(superPowerconection?.sessionId)
                    .then((res) => {
                        setDataPlanning({ label: extractOptions(res?.data?.plan ?? "", [/<title>(.*?)<\/title>/gs])?.[0]?.trim?.(), value: res?.data?.session_id })
                    })
                    .catch(console.error);
            }
            if (superPowerconection?.idClassProfile) {
                await mutateAsyncClassProfile({ id: superPowerconection?.idClassProfile }).then((res) => {
                    setClassProfile(res)
                }).catch(console.error);
            }
            setSuperPowerconection(null);
        }
        _getData();
        // eslint-disable-next-line
    }, [superPowerconection, setSuperPowerconection, mutateFeedBack, mutateAsyncClassProfile]);
    return { classProfile, dataPlanning, isLoadingSession, isLoadingSupper, setClassProfile }
}